/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiError } from "@/lib/api";
import { ChevronLeft, ChevronRight, Plus, Trash2, Printer, CalendarX, CalendarCheck, Star, AlertTriangle, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatDateLongCapitalized, formatDateLong, formatTime } from "@/lib/date";

const KIND_META = {
  rdv:      { color: "#DCB457", bg: "bg-[#DCB457]/15",   icon: CalendarCheck, label: "RDV" },
  event:    { color: "#c88a1e", bg: "bg-amber-500/15",   icon: Star,          label: "Événement" },
  deadline: { color: "#c46a00", bg: "bg-orange-500/15", icon: AlertTriangle, label: "Échéance" },
  blocked:  { color: "#9a2f2f", bg: "bg-red-500/15",    icon: X,             label: "Bloqué" },
};

function monthKey(y, m) { return `${y}-${String(m).padStart(2, "0")}`; }

function useMonthGrid(year, month) {
  return useMemo(() => {
    // JS month is 0-indexed; we receive month 1-12
    const first = new Date(Date.UTC(year, month - 1, 1));
    // Adjust: Monday = 0
    const firstDay = (first.getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const rows = [];
    let day = 1 - firstDay;
    while (day <= daysInMonth) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(day >= 1 && day <= daysInMonth ? day : null);
        day++;
      }
      rows.push(week);
    }
    return rows;
  }, [year, month]);
}

export default function AdminCalendar() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [unavailable, setUnavailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockDate, setBlockDate] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [evs, un] = await Promise.all([
        adminApi.calendarEvents(monthKey(year, month)),
        adminApi.listUnavailable(),
      ]);
      setEvents(evs.events || []);
      setUnavailable(un || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const grid = useMonthGrid(year, month);
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const d = new Date(ev.date_iso);
      const day = d.getUTCDate();
      (map[day] ||= []).push(ev);
    }
    return map;
  }, [events]);

  const goPrev = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const goNext = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth() + 1); };

  const monthLabel = (() => {
    const d = new Date(Date.UTC(year, month - 1, 1));
    const label = d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
    return label.charAt(0).toUpperCase() + label.slice(1);
  })();

  const addBlock = async (e) => {
    e.preventDefault();
    if (!blockDate) return;
    try {
      await adminApi.addUnavailable({ date: blockDate, reason: blockReason });
      setBlockDate(""); setBlockReason(""); setShowBlockForm(false);
      toast.success("Date bloquée.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeBlock = async (date) => {
    if (!window.confirm(`Rendre disponible le ${formatDateLong(date)} ?`)) return;
    try {
      await adminApi.removeUnavailable(date);
      toast.success("Date libérée.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const downloadPdf = async () => {
    const url = adminApi.calendarPdfUrl(monthKey(year, month));
    try {
      const token = localStorage.getItem("ma_token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `planning_${monthKey(year, month)}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    } catch (e) { toast.error("Erreur téléchargement PDF"); }
  };

  const dayEvents = selectedDay ? eventsByDay[selectedDay] || [] : [];
  const isTodayCell = (day) => {
    const t = new Date();
    return t.getFullYear() === year && t.getMonth() + 1 === month && t.getDate() === day;
  };

  return (
    <div data-testid="admin-calendar">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Planning</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Mon <span className="italic gold-text">calendrier.</span></h1>
          <p className="text-white/60 text-sm mt-3 max-w-2xl">
            Vue mensuelle regroupant RDV, événements confirmés, échéances URSSAF et journées bloquées.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={goToday} className="btn-ghost-gold text-xs" data-testid="cal-today">Aujourd'hui</button>
          <button onClick={downloadPdf} className="btn-ghost-gold inline-flex items-center gap-2 text-xs" data-testid="cal-pdf">
            <Printer size={13} strokeWidth={1.5} /> Imprimer PDF
          </button>
        </div>
      </header>

      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <button onClick={goPrev} className="p-2 border border-[#DCB457]/30 hover:bg-[#DCB457]/10" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <h2 className="font-serif-luxe text-2xl min-w-[220px] text-center" data-testid="cal-month-label">{monthLabel}</h2>
          <button onClick={goNext} className="p-2 border border-[#DCB457]/30 hover:bg-[#DCB457]/10" data-testid="cal-next"><ChevronRight size={16} /></button>
        </div>
        <button onClick={() => setShowBlockForm((v) => !v)} className="btn-ghost-gold inline-flex items-center gap-2 text-xs" data-testid="cal-block-toggle">
          <Plus size={13} strokeWidth={1.5} /> Bloquer une date
        </button>
      </div>

      {showBlockForm && (
        <form onSubmit={addBlock} className="card-luxe p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="cal-block-form">
          <input required type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} className="bg-black/60 border border-[#DCB457]/25 px-3 py-2.5 text-sm" data-testid="cal-block-date" />
          <input placeholder="Raison (optionnel)" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} className="md:col-span-2 bg-black/60 border border-[#DCB457]/25 px-3 py-2.5 text-sm" data-testid="cal-block-reason" />
          <button type="submit" className="btn-gold text-xs" data-testid="cal-block-submit">Bloquer</button>
        </form>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-[11px] text-white/60 mb-3">
        {Object.entries(KIND_META).map(([k, m]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5" style={{ backgroundColor: m.color }} /> {m.label}
          </span>
        ))}
      </div>

      {/* Grid */}
      <div className="card-luxe overflow-hidden" data-testid="cal-grid">
        <div className="grid grid-cols-7 border-b border-[#DCB457]/30 text-[10px] uppercase tracking-[0.22em] text-[#DCB457] bg-black/60">
          {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((d) => (
            <div key={d} className="px-2 py-3 text-center">{d}</div>
          ))}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/40">
            <Loader2 className="animate-spin mr-2" /> Chargement…
          </div>
        ) : (
          <div>
            {grid.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 border-b border-[#DCB457]/8 last:border-b-0">
                {week.map((day, di) => {
                  const isWknd = di >= 5;
                  const evs = day ? (eventsByDay[day] || []) : [];
                  const isToday = day && isTodayCell(day);
                  return (
                    <button
                      key={di}
                      type="button"
                      disabled={!day}
                      onClick={() => day && setSelectedDay(day)}
                      className={`min-h-[110px] p-2 text-left align-top border-r border-[#DCB457]/8 last:border-r-0 transition ${
                        day ? "hover:bg-[#DCB457]/8 cursor-pointer" : "bg-black/30 cursor-default"
                      } ${isWknd && day ? "bg-black/50" : ""} ${isToday ? "ring-2 ring-inset ring-[#DCB457]" : ""}`}
                      data-testid={day ? `cal-day-${day}` : undefined}
                    >
                      {day && (
                        <>
                          <div className={`text-sm font-medium mb-1.5 ${isToday ? "text-[#DCB457]" : "text-white/85"}`}>{day}</div>
                          <div className="space-y-1">
                            {evs.slice(0, 3).map((ev) => {
                              const meta = KIND_META[ev.kind] || KIND_META.rdv;
                              return (
                                <div key={ev.id} className={`text-[10px] px-1.5 py-0.5 ${meta.bg} border-l-2 truncate`} style={{ borderLeftColor: meta.color }}>
                                  <span className="font-medium truncate">{ev.title}</span>
                                  {ev.subtitle && <span className="text-white/45"> · {ev.subtitle}</span>}
                                </div>
                              );
                            })}
                            {evs.length > 3 && (
                              <div className="text-[9px] text-[#DCB457]/70">+ {evs.length - 3} autres…</div>
                            )}
                          </div>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Selected day panel */}
      {selectedDay && (
        <div className="card-luxe p-6 mt-6" data-testid="cal-day-detail">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="eyebrow">Journée du</p>
              <h3 className="font-serif-luxe text-2xl mt-1">{formatDateLongCapitalized(`${year}-${String(month).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`)}</h3>
            </div>
            <button onClick={() => setSelectedDay(null)} className="text-white/50 hover:text-white p-1"><X size={16} /></button>
          </div>
          {dayEvents.length === 0 ? (
            <p className="text-sm text-white/50 italic">Aucun événement ce jour-là.</p>
          ) : (
            <ul className="space-y-3">
              {dayEvents.map((ev) => {
                const meta = KIND_META[ev.kind] || KIND_META.rdv;
                const Icon = meta.icon;
                const time = ev.date_iso ? formatTime(ev.date_iso) : "";
                return (
                  <li key={ev.id} className="flex items-start gap-3 p-3 border-l-2" style={{ borderLeftColor: meta.color }}>
                    <Icon size={16} style={{ color: meta.color }} className="mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: meta.color }}>{meta.label}</span>
                        {time && <span className="text-[10px] text-white/50">{time}</span>}
                      </div>
                      <p className="font-medium mt-1">{ev.title}</p>
                      {ev.subtitle && <p className="text-sm text-white/60">{ev.subtitle}</p>}
                      {ev.detail && <p className="text-xs text-white/45 mt-1">{ev.detail}</p>}
                      {ev.amount ? <p className="text-xs text-[#DCB457] mt-1">{ev.amount.toLocaleString("fr-FR")} €</p> : null}
                    </div>
                    {ev.url && (
                      <Link to={ev.url} className="text-[10px] uppercase tracking-[0.18em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1 hover:bg-[#DCB457]/10">
                        Voir
                      </Link>
                    )}
                    {ev.kind === "blocked" && (
                      <button onClick={() => removeBlock(ev.date_iso.slice(0, 10))} className="text-red-300 text-[10px] uppercase tracking-[0.18em] border border-red-400/30 px-2 py-1 hover:bg-red-500/10">
                        Libérer
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* All blocked dates (full list) */}
      {unavailable.length > 0 && (
        <details className="card-luxe p-5 mt-6" data-testid="cal-blocked-list">
          <summary className="cursor-pointer text-sm text-white/70 hover:text-[#DCB457] flex items-center gap-2">
            <CalendarX size={14} /> {unavailable.length} date(s) bloquée(s) toutes années confondues
          </summary>
          <ul className="mt-4 space-y-1.5 text-sm">
            {unavailable.map((u) => (
              <li key={u.date} className="flex items-center justify-between py-1.5 border-t border-[#DCB457]/10">
                <span>{formatDateLong(u.date)} <span className="text-white/45 text-xs">— {u.reason || "—"}</span></span>
                <button onClick={() => removeBlock(u.date)} className="text-red-300 inline-flex items-center gap-1 border border-red-400/30 px-2 py-1 text-xs hover:bg-red-500/10">
                  <Trash2 size={11} /> Libérer
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
